import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';

const API = axios.create({ baseURL: '/api', withCredentials: true });

const emptyPiece = { weight: '', length_in: '', width_in: '', height_in: '' };

// ── Piece Entry Modal ──────────────────────────────────────────
const PieceModal = ({ row, customerId, onConfirm, onClose }) => {
  const [pieces, setPieces] = useState(
    row.pieces && row.pieces.length > 0
      ? row.pieces.map(p => ({ ...p }))
      : [{ ...emptyPiece, id: Date.now() }]
  );
  const [preview, setPreview] = useState(null);
  const previewTimer = React.useRef(null);
  const addPiece = () => setPieces(prev => [...prev, { ...emptyPiece, id: Date.now() }]);
  const removePiece = (key) => {
    if (pieces.length === 1) return toast.error('At least one piece is required');
    setPieces(prev => prev.filter((p, i) => (p.id || i) !== key));
  };
  const updatePiece = (key, field, value) => {
    setPieces(prev => prev.map((p, i) => (p.id || i) === key ? { ...p, [field]: value } : p));
  };
  useEffect(() => {
    clearTimeout(previewTimer.current);
    if (!row.destination_zip || row.destination_zip.length < 5) return;
    const validPieces = pieces.filter(p => p.weight);
    if (validPieces.length === 0) return;
    previewTimer.current = setTimeout(async () => {
      try {
        const res = await API.post('/invoices/preview', {
          customer_id: customerId,
          destination_zip: row.destination_zip,
          is_residential: row.is_residential,
          inside_charges: row.inside_charges,
          unpack_debris: row.unpack_debris,
          liftgate_mode: row.liftgate_mode || 'auto',
          special_applied: row.special_applied || false,
          special_time: row.special_time || 'normal',
          two_man_mode: row.two_man_mode || 'auto',
          misc_charges: parseFloat(row.misc_charges) || 0,
          special_charges: parseFloat(row.special_charges) || 0,
          pieces: validPieces.map(p => ({
            weight: parseFloat(p.weight) || 0,
            length_in: parseFloat(p.length_in) || 0,
            width_in: parseFloat(p.width_in) || 0,
            height_in: parseFloat(p.height_in) || 0
          }))
        });
        setPreview(res.data);
      } catch {
        setPreview(null);
      }
    }, 600);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pieces, row.destination_zip]);
  const totalActual = pieces.reduce((sum, p) => sum + (parseFloat(p.weight) || 0), 0);
  const mInput = { width: '100%', padding: '6px 8px', border: '1px solid #E2E8F0', borderRadius: '4px', fontSize: '13px', outline: 'none' };
  const mTh = { padding: '8px', background: '#1A2A3A', color: 'white', fontSize: '12px', fontWeight: '500', textAlign: 'left', borderRight: '1px solid #F5A800' };
  const card = { background: 'white', border: '1px solid #E2E8F0', borderRadius: '6px', padding: '10px 14px' };
  const cardLabel = { fontSize: '11px', color: '#5B6B7F', marginBottom: '2px' };
  const cardVal = { fontWeight: '600', fontSize: '16px' };
  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: 'white', borderRadius: '12px', width: '700px', maxHeight: '85vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
        <div style={{ padding: '20px 24px', borderBottom: '1px solid #E2E8F0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h2 style={{ fontSize: '18px', fontWeight: '600' }}>Piece Entry</h2>
            <p style={{ fontSize: '13px', color: '#5B6B7F', marginTop: '2px' }}>
              ZIP: <strong>{row.destination_zip || '—'}</strong>
            </p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#5B6B7F' }}>✕</button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 24px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr>
                <th style={{ ...mTh, width: '40px' }}>#</th>
                <th style={mTh}>Weight (lbs)</th>
                <th style={mTh}>Length (in)</th>
                <th style={mTh}>Width (in)</th>
                <th style={mTh}>Height (in)</th>
                <th style={{ ...mTh, borderRight: 'none', width: '40px' }}></th>
              </tr>
            </thead>
            <tbody>
              {pieces.map((piece, idx) => {
                const key = piece.id || idx;
                return (
                  <tr key={key}>
                    <td style={{ padding: '6px 8px', borderBottom: '1px solid #E2E8F0', color: '#5B6B7F', fontSize: '12px', fontWeight: '600' }}>{idx + 1}</td>
                    <td style={{ padding: '4px 8px', borderBottom: '1px solid #E2E8F0' }}>
                      <input style={mInput} type="number" value={piece.weight} onChange={e => updatePiece(key, 'weight', e.target.value)} placeholder="0" autoFocus={idx === pieces.length - 1} />
                    </td>
                    <td style={{ padding: '4px 8px', borderBottom: '1px solid #E2E8F0' }}>
                      <input style={mInput} type="number" value={piece.length_in} onChange={e => updatePiece(key, 'length_in', e.target.value)} placeholder="0" />
                    </td>
                    <td style={{ padding: '4px 8px', borderBottom: '1px solid #E2E8F0' }}>
                      <input style={mInput} type="number" value={piece.width_in} onChange={e => updatePiece(key, 'width_in', e.target.value)} placeholder="0" />
                    </td>
                    <td style={{ padding: '4px 8px', borderBottom: '1px solid #E2E8F0' }}>
                      <input style={mInput} type="number" value={piece.height_in} onChange={e => updatePiece(key, 'height_in', e.target.value)} placeholder="0" />
                    </td>
                    <td style={{ padding: '4px 8px', borderBottom: '1px solid #E2E8F0', textAlign: 'center' }}>
                      <button onClick={() => removePiece(key)} style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: '16px' }}>✕</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <button onClick={addPiece} style={{ marginTop: '10px', background: 'none', border: '1px dashed #F5A800', color: '#F5A800', borderRadius: '6px', padding: '7px 16px', cursor: 'pointer', fontSize: '13px', width: '100%' }}>
            + Add Piece
          </button>
        </div>
        <div style={{ padding: '16px 24px', borderTop: '1px solid #E2E8F0', background: '#F4F7FB' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px', marginBottom: '16px' }}>
            <div style={card}><p style={cardLabel}>Pieces</p><p style={cardVal}>{pieces.length}</p></div>
            <div style={card}><p style={cardLabel}>Total Actual Wt</p><p style={cardVal}>{totalActual.toFixed(0)} lbs</p></div>
            <div style={card}><p style={cardLabel}>Total Dim Wt</p><p style={cardVal}>{preview ? `${preview.total_dim_weight} lbs` : '—'}</p></div>
            <div style={card}><p style={cardLabel}>Billable Wt</p><p style={cardVal}>{preview ? `${preview.billable_weight} lbs` : '—'}</p></div>
            <div style={card}><p style={cardLabel}>Liftgate</p><p style={{ ...cardVal, color: preview?.liftgate_auto ? '#dc2626' : '#15803D' }}>{preview ? (preview.liftgate_auto ? 'YES' : 'No') : '—'}</p></div>
            <div style={card}><p style={cardLabel}>Two-Man</p><p style={{ ...cardVal, color: preview?.two_man_auto ? '#dc2626' : '#15803D' }}>{preview ? (preview.two_man_auto ? 'YES' : 'No') : '—'}</p></div>
            <div style={{ background: '#E9F7EF', border: '1px solid #A7D9BC', borderRadius: '6px', padding: '10px 14px' }}>
              <p style={cardLabel}>Est. Total</p>
              <p style={{ fontWeight: '700', fontSize: '18px', color: '#15803D' }}>{preview ? `$${preview.final_total}` : '...'}</p>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <button onClick={onClose} style={{ background: 'none', border: '1px solid #E2E8F0', borderRadius: '6px', padding: '9px 20px', cursor: 'pointer', fontSize: '14px', color: '#0F1B2D' }}>Cancel</button>
            <button
              onClick={() => onConfirm(pieces.map(p => ({
                weight: parseFloat(p.weight) || 0,
                length_in: parseFloat(p.length_in) || 0,
                width_in: parseFloat(p.width_in) || 0,
                height_in: parseFloat(p.height_in) || 0
              })))}
              className="btn btn-primary"
              style={{ width: 'auto', padding: '9px 24px' }}
            >
              Confirm Pieces ({pieces.length})
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Main Quote Editor ──────────────────────────────────────────
const QuoteEdit = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [quote, setQuote] = useState(null);
  const [lines, setLines] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editRow, setEditRow] = useState({});
  const [newRows, setNewRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [previews, setPreviews] = useState({});
  const [pieceModal, setPieceModal] = useState(null);
  const previewTimers = React.useRef({});

  const fetchQuote = React.useCallback(async () => {
    try {
      const res = await API.get(`/quotes/${id}`);
      setQuote(res.data);
      setLines(res.data.lines || []);
    } catch {
      toast.error('Quote not found');
      navigate('/quotes');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { fetchQuote(); }, [fetchQuote]);

  const customerId = quote?.customer_id;

  const fetchPreview = (row, rowId) => {
    const validPieces = Array.isArray(row.pieces) ? row.pieces.filter(p => parseFloat(p.weight) > 0) : [];
    if (!row.destination_zip || row.destination_zip.length < 5 || validPieces.length === 0) {
      setPreviews(prev => ({ ...prev, [rowId]: null }));
      return;
    }
    clearTimeout(previewTimers.current[rowId]);
    previewTimers.current[rowId] = setTimeout(async () => {
      try {
        const res = await API.post('/invoices/preview', {
          customer_id: customerId,
          destination_zip: row.destination_zip,
          is_residential: row.is_residential,
          inside_charges: row.inside_charges,
          unpack_debris: row.unpack_debris,
          liftgate_mode: row.liftgate_mode || 'auto',
          special_applied: row.special_applied || false,
          special_time: row.special_time || 'normal',
          two_man_mode: row.two_man_mode || 'auto',
          misc_charges: parseFloat(row.misc_charges) || 0,
          special_charges: parseFloat(row.special_charges) || 0,
          pieces: validPieces.map(p => ({
            weight: parseFloat(p.weight) || 0,
            length_in: parseFloat(p.length_in) || 0,
            width_in: parseFloat(p.width_in) || 0,
            height_in: parseFloat(p.height_in) || 0
          }))
        });
        setPreviews(prev => ({ ...prev, [rowId]: res.data }));
      } catch {
        setPreviews(prev => ({ ...prev, [rowId]: null }));
      }
    }, 600);
  };

  const startEdit = (line) => {
    setEditingId(line.line_id);
    const withWaivers = {
      ...line,
      liftgate_mode: line.liftgate_mode || 'auto',
      special_applied: line.special_applied || false,
      special_time: line.special_time || 'normal',
      two_man_mode: line.two_man_mode || 'auto'
    };
    setEditRow(withWaivers);
    fetchPreview(withWaivers, `edit_${line.line_id}`);
  };
  const cancelEdit = () => { setEditingId(null); setEditRow({}); };
  const updateEditField = (field, value) => {
    setEditRow(prev => {
      const updated = { ...prev, [field]: value };
      fetchPreview(updated, `edit_${prev.line_id}`);
      return updated;
    });
  };

  const saveEdit = async () => {
    if (!Array.isArray(editRow.pieces) || editRow.pieces.length === 0) {
      return toast.error('At least one piece is required');
    }
    setSaving(true);
    try {
      const res = await API.put(`/quotes/${id}/lines/${editingId}`, {
        ...editRow,
        pieces: editRow.pieces.map(p => ({
          weight: parseFloat(p.weight) || 0,
          length_in: parseFloat(p.length_in) || 0,
          width_in: parseFloat(p.width_in) || 0,
          height_in: parseFloat(p.height_in) || 0
        })),
        misc_charges: parseFloat(editRow.misc_charges) || 0,
        special_charges: parseFloat(editRow.special_charges) || 0
      });
      setLines(prev => prev.map(s => s.line_id === editingId ? res.data.line : s));
      const newTotal = lines
        .map(s => s.line_id === editingId ? parseFloat(res.data.line.final_total) : parseFloat(s.final_total))
        .reduce((a, b) => a + b, 0);
      setQuote(prev => ({ ...prev, amount: newTotal.toFixed(2) }));
      setEditingId(null);
      setEditRow({});
      toast.success('Line updated');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update line');
    } finally {
      setSaving(false);
    }
  };

  const updateQuoteHeader = async () => {
    setSaving(true);
    try {
      await API.put(`/quotes/${id}`, {
        bill_to: quote.bill_to,
        quote_date: quote.quote_date,
        valid_until: quote.valid_until || null
      });
      toast.success('Quote header updated');
    } catch {
      toast.error('Failed to update quote');
    } finally {
      setSaving(false);
    }
  };

  const deleteLine = async (lineId, finalTotal) => {
    if (!window.confirm('Remove this line?')) return;
    try {
      await API.delete(`/quotes/${id}/lines/${lineId}`);
      setLines(prev => prev.filter(s => s.line_id !== lineId));
      setQuote(prev => ({ ...prev, amount: Math.max(0, parseFloat(prev.amount) - parseFloat(finalTotal)).toFixed(2) }));
      toast.success('Line removed');
    } catch {
      toast.error('Failed to remove line');
    }
  };

  const addNewRow = () => {
    setNewRows(prev => [...prev, {
      destination_zip: '', pieces: [],
      is_residential: false, pickup_or_delivery: 'D',
      inside_charges: false, unpack_debris: false,
      liftgate_mode: 'auto', two_man_mode: 'auto',
      special_applied: false, special_time: 'normal',
      misc_charges: '', special_charges: '', misc_notes: '', special_notes: '',
      id: Date.now()
    }]);
  };
  const updateNewRow = (rowId, field, value) => {
    setNewRows(prev => {
      const updated = prev.map(r => r.id === rowId ? { ...r, [field]: value } : r);
      const changed = updated.find(r => r.id === rowId);
      fetchPreview(changed, `new_${rowId}`);
      return updated;
    });
  };

  const saveNewLine = async (row) => {
    if (!row.destination_zip) return toast.error('ZIP required');
    if (!Array.isArray(row.pieces) || row.pieces.length === 0) return toast.error('Add at least one piece first');
    setSaving(true);
    try {
      const res = await API.post(`/quotes/${id}/lines`, {
        ...row,
        pieces: row.pieces.map(p => ({
          weight: parseFloat(p.weight) || 0,
          length_in: parseFloat(p.length_in) || 0,
          width_in: parseFloat(p.width_in) || 0,
          height_in: parseFloat(p.height_in) || 0
        })),
        misc_charges: parseFloat(row.misc_charges) || 0,
        special_charges: parseFloat(row.special_charges) || 0
      });
      setLines(prev => [...prev, res.data.line]);
      setNewRows(prev => prev.filter(r => r.id !== row.id));
      setQuote(prev => ({ ...prev, amount: (parseFloat(prev.amount) + parseFloat(res.data.line.final_total)).toFixed(2) }));
      toast.success('Line added');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to add line');
    } finally {
      setSaving(false);
    }
  };

  const openPieces = (mode, rowId) => {
    const target = mode === 'edit' ? editRow : newRows.find(r => r.id === rowId);
    if (!target || !target.destination_zip || target.destination_zip.length < 5) {
      return toast.error('Enter a valid ZIP code before adding pieces');
    }
    setPieceModal({ mode, id: rowId });
  };
  const confirmPieces = (pieces) => {
    if (!pieceModal) return;
    if (pieceModal.mode === 'edit') updateEditField('pieces', pieces);
    else updateNewRow(pieceModal.id, 'pieces', pieces);
    setPieceModal(null);
    toast.success(`${pieces.length} piece${pieces.length !== 1 ? 's' : ''} confirmed`);
  };
  const modalRow = pieceModal
    ? (pieceModal.mode === 'edit' ? editRow : newRows.find(r => r.id === pieceModal.id))
    : null;

  const piecesBtnStyle = (count) => ({
    background: count > 0 ? '#E9F7EF' : '#FDF4DD',
    border: `1px solid ${count > 0 ? '#A7D9BC' : '#F2D89A'}`,
    color: count > 0 ? '#15803D' : '#F5A800',
    borderRadius: '6px', padding: '5px 10px', cursor: 'pointer',
    fontSize: '12px', fontWeight: '500', whiteSpace: 'nowrap'
  });
  const inputStyle = { width: '100%', padding: '5px 6px', border: '1px solid #E8B84B', borderRadius: '4px', fontSize: '12px', outline: 'none', background: '#FDF4DD' };
  const thStyle = { padding: '10px 8px', background: '#1A2A3A', color: 'white', fontSize: '12px', fontWeight: '500', whiteSpace: 'nowrap', textAlign: 'left', borderRight: '1px solid #F5A800' };
  const thPreviewStyle = { ...thStyle, background: '#3A3C3F' };
  const tdStyle = { padding: '6px 8px', borderBottom: '1px solid #E2E8F0', borderRight: '1px solid #f0f0f0', fontSize: '13px', verticalAlign: 'middle' };
  const tdEditStyle = { ...tdStyle, background: '#FDF4DD', padding: '4px 6px' };
  const tdPreviewStyle = { ...tdStyle, background: '#FDF4DD', fontSize: '12px' };

  if (loading) return <div style={{ padding: '40px', textAlign: 'center' }}>Loading...</div>;
  if (!quote) return null;

  const modeSelectStyle = { fontSize: '11px', padding: '2px 4px', border: '1px solid #E8B84B', borderRadius: '4px', background: '#FDF4DD', outline: 'none', cursor: 'pointer' };

  const modeCell = (isEditing, p, row, modeField, autoKey, feeKey, onChange) => {
    if (isEditing) {
      if (!p) return '...';
      const mode = row[modeField] || 'auto';
      return (
        <select value={mode} onChange={e => onChange(e.target.value)} style={modeSelectStyle}>
          <option value="auto">Auto {p[autoKey] ? '(on)' : '(off)'}</option>
          <option value="waived">Waived</option>
          <option value="applied">Applied</option>
        </select>
      );
    }
    if (parseFloat(row[feeKey]) > 0) return '✓';
    if ((row[modeField] || 'auto') === 'waived') return 'waived';
    return '—';
  };

  return (
    <div style={{ padding: '24px', maxWidth: '100%' }}>
      {pieceModal && modalRow && (
        <PieceModal row={modalRow} customerId={customerId} onConfirm={confirmPieces} onClose={() => setPieceModal(null)} />
      )}

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
        <div style={{ flex: 1, marginRight: '24px' }}>
          <h1 style={{ fontSize: '22px', fontWeight: '600', marginBottom: '16px' }}>Edit {quote.quote_number}</h1>
          <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: '8px', padding: '16px 20px' }}>
            <h2 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '12px', color: '#5B6B7F' }}>Quote Details</h2>
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', marginBottom: '4px', color: '#5B6B7F' }}>Bill To</label>
                <input style={{ padding: '7px 10px', border: '1px solid #E2E8F0', borderRadius: '6px', fontSize: '14px', outline: 'none', width: '200px' }}
                  value={quote.bill_to || ''} onChange={e => setQuote(prev => ({ ...prev, bill_to: e.target.value }))} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', marginBottom: '4px', color: '#5B6B7F' }}>Quote Date</label>
                <input type="date" style={{ padding: '7px 10px', border: '1px solid #E2E8F0', borderRadius: '6px', fontSize: '14px', outline: 'none', width: '160px' }}
                  value={quote.quote_date ? quote.quote_date.split('T')[0] : ''} onChange={e => setQuote(prev => ({ ...prev, quote_date: e.target.value }))} />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '12px', fontWeight: '500', marginBottom: '4px', color: '#5B6B7F' }}>Valid Until</label>
                <input type="date" style={{ padding: '7px 10px', border: '1px solid #E2E8F0', borderRadius: '6px', fontSize: '14px', outline: 'none', width: '160px' }}
                  value={quote.valid_until ? quote.valid_until.split('T')[0] : ''} onChange={e => setQuote(prev => ({ ...prev, valid_until: e.target.value }))} />
              </div>
              <button onClick={updateQuoteHeader} disabled={saving} className="btn btn-primary" style={{ width: 'auto', padding: '8px 18px', fontSize: '13px' }}>
                Save Header
              </button>
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', paddingTop: '4px' }}>
          <div style={{ textAlign: 'right', marginRight: '8px' }}>
            <p style={{ fontSize: '12px', color: '#5B6B7F' }}>Estimated Total</p>
            <p style={{ fontWeight: '700', fontSize: '22px', color: '#15803D' }}>${parseFloat(quote.amount).toFixed(2)}</p>
          </div>
          <button onClick={() => navigate(`/quotes/${id}`)} style={{ background: 'none', border: '1px solid #E2E8F0', borderRadius: '6px', padding: '8px 16px', cursor: 'pointer', fontSize: '14px' }}>
            ← View Quote
          </button>
          <button onClick={addNewRow} style={{ background: '#F5A800', color: 'white', border: 'none', borderRadius: '6px', padding: '8px 16px', cursor: 'pointer', fontSize: '14px' }}>
            + Add Line
          </button>
        </div>
      </div>

      {/* Lines table */}
      <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: '8px', overflow: 'hidden', marginBottom: '20px' }}>
        <div style={{ padding: '10px 16px', borderBottom: '1px solid #E2E8F0', background: '#F4F7FB' }}>
          <p style={{ fontSize: '13px', color: '#5B6B7F' }}>
            Click <strong>Edit</strong> on any row to modify it. Use <strong>Pieces</strong> to change weights and dimensions. Changes recalculate automatically.
          </p>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr>
                <th style={thStyle}>ZIP</th>
                <th style={thStyle}>Pieces</th>
                <th style={thStyle}>Res</th>
                <th style={thStyle}>P/D</th>
                <th style={thStyle}>Inside</th>
                <th style={thStyle}>Unpack</th>
                <th style={thStyle}>Misc $</th>
                <th style={thStyle}>Special</th>
                <th style={thStyle}>Notes</th>
                <th style={thPreviewStyle}>Zone</th>
                <th style={thPreviewStyle}>Bill. Wt</th>
                <th style={thPreviewStyle}>Lift</th>
                <th style={thPreviewStyle}>2-Man</th>
                <th style={thPreviewStyle}>Total</th>
                <th style={{ ...thStyle, borderRight: 'none' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {lines.map(s => {
                const isEditing = editingId === s.line_id;
                const row = isEditing ? editRow : s;
                const p = isEditing ? previews[`edit_${s.line_id}`] : null;
                const td = isEditing ? tdEditStyle : tdStyle;
                const pvTd = isEditing ? tdPreviewStyle : tdStyle;
                const pieceCount = Array.isArray(row.pieces) ? row.pieces.length : 0;
                return (
                  <tr key={s.line_id} style={{ background: isEditing ? '#FDF4DD' : 'white' }}>
                    <td style={td}>
                      {isEditing
                        ? <input style={{ ...inputStyle, width: '70px' }} value={row.destination_zip} onChange={e => updateEditField('destination_zip', e.target.value)} maxLength={5} />
                        : row.destination_zip}
                    </td>
                    <td style={td}>
                      {isEditing
                        ? <button onClick={() => openPieces('edit', s.line_id)} style={piecesBtnStyle(pieceCount)}>{pieceCount > 0 ? `✓ ${pieceCount} pc${pieceCount !== 1 ? 's' : ''}` : '+ Pieces'}</button>
                        : pieceCount}
                    </td>
                    <td style={{ ...td, textAlign: 'center' }}>
                      {isEditing
                        ? <input type="checkbox" checked={row.is_residential} onChange={e => updateEditField('is_residential', e.target.checked)} />
                        : row.is_residential ? '✓' : '—'}
                    </td>
                    <td style={td}>
                      {isEditing
                        ? <select style={{ ...inputStyle, width: '55px' }} value={row.pickup_or_delivery} onChange={e => updateEditField('pickup_or_delivery', e.target.value)}>
                            <option value="D">D</option><option value="P">P</option>
                          </select>
                        : row.pickup_or_delivery}
                    </td>
                    <td style={{ ...td, textAlign: 'center' }}>
                      {isEditing
                        ? <input type="checkbox" checked={row.inside_charges} onChange={e => updateEditField('inside_charges', e.target.checked)} />
                        : row.inside_charges ? '✓' : '—'}
                    </td>
                    <td style={{ ...td, textAlign: 'center' }}>
                      {isEditing
                        ? <input type="checkbox" checked={row.unpack_debris} onChange={e => updateEditField('unpack_debris', e.target.checked)} />
                        : row.unpack_debris ? '✓' : '—'}
                    </td>
                    <td style={td}>
                      {isEditing
                        ? <input style={{ ...inputStyle, width: '65px' }} type="number" value={row.misc_charges} onChange={e => updateEditField('misc_charges', e.target.value)} />
                        : `$${parseFloat(row.misc_charges).toFixed(2)}`}
                    </td>
                    <td style={td}>
                      {isEditing ? (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                            <input type="checkbox" checked={!!row.special_applied} onChange={e => updateEditField('special_applied', e.target.checked)} />
                            Special
                          </label>
                          {row.special_applied && p && ['A', 'B', 'C', 'D'].includes(p.zone) && (
                            <select style={{ ...inputStyle, width: '115px' }} value={row.special_time || 'normal'} onChange={e => updateEditField('special_time', e.target.value)}>
                              <option value="normal">Normal Hours</option>
                              <option value="after_hours">After Hours</option>
                              <option value="weekend">Weekend/Holiday</option>
                            </select>
                          )}
                          {row.special_applied && p && !['A', 'B', 'C', 'D'].includes(p.zone) && (
                            <span style={{ fontSize: '10px', color: '#5B6B7F', whiteSpace: 'nowrap' }}>{p.approx_miles} mi × rate</span>
                          )}
                        </div>
                      ) : `$${parseFloat(row.special_charges).toFixed(2)}`}
                    </td>
                    <td style={td}>
                      {isEditing
                        ? <input style={{ ...inputStyle, width: '130px' }} value={row.misc_notes || ''} onChange={e => updateEditField('misc_notes', e.target.value)} placeholder="Notes..." />
                        : ([row.misc_notes, row.special_notes].map(x => (x ? String(x).trim() : '')).filter(Boolean).join(' — ') || '—')}
                    </td>
                    <td style={pvTd}>{isEditing ? (p ? p.zone : '...') : row.zone}</td>
                    <td style={pvTd}>{isEditing ? (p ? p.billable_weight : '...') : row.billable_weight}</td>
                    <td style={pvTd}>{modeCell(isEditing, p, row, 'liftgate_mode', 'liftgate_auto', 'liftgate_fee', v => updateEditField('liftgate_mode', v))}</td>
                    <td style={pvTd}>{modeCell(isEditing, p, row, 'two_man_mode', 'two_man_auto', 'two_man_charge', v => updateEditField('two_man_mode', v))}</td>
                    <td style={{ ...pvTd, fontWeight: '600', color: '#15803D' }}>
                      {isEditing ? (p ? `$${p.final_total}` : `$${parseFloat(row.final_total).toFixed(2)}`) : `$${parseFloat(row.final_total).toFixed(2)}`}
                    </td>
                    <td style={{ ...tdStyle, borderRight: 'none', whiteSpace: 'nowrap' }}>
                      {isEditing ? (
                        <>
                          <button onClick={saveEdit} disabled={saving} style={{ background: '#15803D', color: 'white', border: 'none', borderRadius: '4px', padding: '4px 10px', cursor: 'pointer', fontSize: '12px', marginRight: '4px' }}>Save</button>
                          <button onClick={cancelEdit} style={{ background: '#5B6B7F', color: 'white', border: 'none', borderRadius: '4px', padding: '4px 8px', cursor: 'pointer', fontSize: '12px' }}>Cancel</button>
                        </>
                      ) : (
                        <>
                          <button onClick={() => startEdit(s)} style={{ background: '#f59e0b', color: 'white', border: 'none', borderRadius: '4px', padding: '4px 10px', cursor: 'pointer', fontSize: '12px', marginRight: '4px' }}>Edit</button>
                          <button onClick={() => deleteLine(s.line_id, s.final_total)} style={{ background: '#dc2626', color: 'white', border: 'none', borderRadius: '4px', padding: '4px 8px', cursor: 'pointer', fontSize: '12px' }}>✕</button>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
              {/* New rows */}
              {newRows.map(row => {
                const p = previews[`new_${row.id}`];
                const pieceCount = Array.isArray(row.pieces) ? row.pieces.length : 0;
                return (
                  <tr key={row.id} style={{ background: '#E9F7EF' }}>
                    <td style={tdEditStyle}><input style={{ ...inputStyle, width: '70px', borderColor: '#A7D9BC', background: '#E9F7EF' }} value={row.destination_zip} onChange={e => updateNewRow(row.id, 'destination_zip', e.target.value)} placeholder="33508" maxLength={5} /></td>
                    <td style={tdEditStyle}>
                      <button onClick={() => openPieces('new', row.id)} style={piecesBtnStyle(pieceCount)}>{pieceCount > 0 ? `✓ ${pieceCount} pc${pieceCount !== 1 ? 's' : ''}` : '+ Pieces'}</button>
                    </td>
                    <td style={{ ...tdEditStyle, textAlign: 'center' }}><input type="checkbox" checked={row.is_residential} onChange={e => updateNewRow(row.id, 'is_residential', e.target.checked)} /></td>
                    <td style={tdEditStyle}>
                      <select style={{ ...inputStyle, width: '55px', borderColor: '#A7D9BC', background: '#E9F7EF' }} value={row.pickup_or_delivery} onChange={e => updateNewRow(row.id, 'pickup_or_delivery', e.target.value)}>
                        <option value="D">D</option><option value="P">P</option>
                      </select>
                    </td>
                    <td style={{ ...tdEditStyle, textAlign: 'center' }}><input type="checkbox" checked={row.inside_charges} onChange={e => updateNewRow(row.id, 'inside_charges', e.target.checked)} /></td>
                    <td style={{ ...tdEditStyle, textAlign: 'center' }}><input type="checkbox" checked={row.unpack_debris} onChange={e => updateNewRow(row.id, 'unpack_debris', e.target.checked)} /></td>
                    <td style={tdEditStyle}><input style={{ ...inputStyle, width: '65px', borderColor: '#A7D9BC', background: '#E9F7EF' }} type="number" value={row.misc_charges} onChange={e => updateNewRow(row.id, 'misc_charges', e.target.value)} placeholder="0.00" /></td>
                    <td style={tdEditStyle}>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                          <input type="checkbox" checked={!!row.special_applied} onChange={e => updateNewRow(row.id, 'special_applied', e.target.checked)} />
                          Special
                        </label>
                        {row.special_applied && p && ['A', 'B', 'C', 'D'].includes(p.zone) && (
                          <select style={{ ...inputStyle, width: '115px', borderColor: '#A7D9BC', background: '#E9F7EF' }} value={row.special_time || 'normal'} onChange={e => updateNewRow(row.id, 'special_time', e.target.value)}>
                            <option value="normal">Normal Hours</option>
                            <option value="after_hours">After Hours</option>
                            <option value="weekend">Weekend/Holiday</option>
                          </select>
                        )}
                        {row.special_applied && p && !['A', 'B', 'C', 'D'].includes(p.zone) && (
                          <span style={{ fontSize: '10px', color: '#5B6B7F', whiteSpace: 'nowrap' }}>{p.approx_miles} mi × rate</span>
                        )}
                      </div>
                    </td>
                    <td style={tdEditStyle}><input style={{ ...inputStyle, width: '130px', borderColor: '#A7D9BC', background: '#E9F7EF' }} value={row.misc_notes || ''} onChange={e => updateNewRow(row.id, 'misc_notes', e.target.value)} placeholder="Notes..." /></td>
                    <td style={tdPreviewStyle}>{p ? p.zone : '—'}</td>
                    <td style={tdPreviewStyle}>{p ? p.billable_weight : '—'}</td>
                    <td style={tdPreviewStyle}>{modeCell(true, p, row, 'liftgate_mode', 'liftgate_auto', 'liftgate_fee', v => updateNewRow(row.id, 'liftgate_mode', v))}</td>
                    <td style={tdPreviewStyle}>{modeCell(true, p, row, 'two_man_mode', 'two_man_auto', 'two_man_charge', v => updateNewRow(row.id, 'two_man_mode', v))}</td>
                    <td style={{ ...tdPreviewStyle, fontWeight: '600', color: p ? '#15803D' : '#8595A6' }}>{p ? `$${p.final_total}` : '...'}</td>
                    <td style={{ ...tdEditStyle, borderRight: 'none', whiteSpace: 'nowrap' }}>
                      <button onClick={() => saveNewLine(row)} disabled={saving} style={{ background: '#15803D', color: 'white', border: 'none', borderRadius: '4px', padding: '4px 10px', cursor: 'pointer', fontSize: '12px', marginRight: '4px' }}>Save</button>
                      <button onClick={() => setNewRows(prev => prev.filter(r => r.id !== row.id))} style={{ background: '#dc2626', color: 'white', border: 'none', borderRadius: '4px', padding: '4px 8px', cursor: 'pointer', fontSize: '12px' }}>✕</button>
                    </td>
                  </tr>
                );
              })}
              {/* Total row */}
              <tr style={{ background: '#F4F7FB' }}>
                <td colSpan={13} style={{ ...tdStyle, fontWeight: '600', textAlign: 'right', borderRight: 'none' }}>
                  Estimated Total:
                </td>
                <td colSpan={2} style={{ ...tdStyle, fontWeight: '700', color: '#15803D', fontSize: '15px', borderRight: 'none' }}>
                  ${parseFloat(quote.amount).toFixed(2)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default QuoteEdit;
